import { notification } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DispositionPendingReview,
  EmailFilterOptions,
  InboundEmail,
  InboundEmailAttachment,
  ListEmailsRequest,
  TagAutomation,
} from '../api/models';
import APIClient from '../api/api';
import { debounce } from 'lodash';
import { formatDateTime } from '../utils';
import { Column, DataTable, Pagination, StatusTag } from './ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import EmailDetailModal from './EmailDetailModal';
import { classificationLabel, RoutingCell } from './EmailRouting';
import { MultiSelectPill, SelectPill } from './FilterPills';
import {
  defaultEmailFilters,
  EmailFilters,
  filtersFromParams,
  filtersToParams,
  listRequestForFilters,
} from './Emails.filters';

export interface Props {
  filterTag?: string;
  filterAllTags?: string;
  excludeTag?: string;
  title?: string;
  description?: string;
  tagFilters?: boolean;
}

const PAGE_SIZE = 25;

const dispositionOptions = [
  { value: '', label: 'Any' },
  { value: DispositionPendingReview, label: 'Pending review' },
];

const Emails = (props: Props) => {
  const [emails, setEmails] = useState<InboundEmail[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [localFilters, setLocalFilters] = useState<EmailFilters>(defaultEmailFilters);
  const [filterOptions, setFilterOptions] = useState<EmailFilterOptions>();
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = props.tagFilters ? filtersFromParams(searchParams) : localFilters;
  const { search, disposition } = filters;

  const request: ListEmailsRequest = props.tagFilters
    ? listRequestForFilters(filters)
    : { tag: props.filterTag, tagAll: props.filterAllTags, excludeTag: props.excludeTag, search: search || undefined, disposition: disposition || undefined };
  const requestKey = JSON.stringify(request);

  function updateFilters(patch: Partial<EmailFilters>) {
    if (props.tagFilters) {
      setSearchParams((prev) => filtersToParams(prev, { ...filtersFromParams(prev), ...patch }), { replace: true });
    } else {
      setLocalFilters((prev) => ({ ...prev, ...patch }));
    }
  }
  // The debounced search outlives renders, so it must reach the latest setter.
  const updateFiltersRef = useRef(updateFilters);
  updateFiltersRef.current = updateFilters;
  const selectedEmailId = searchParams.get('email') ? Number(searchParams.get('email')) : null;

  function setSelectedEmailId(id: number | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id != null) next.set('email', String(id));
      else next.delete('email');
      return next;
    }, { replace: true });
  }

  useEffect(() => {
    loadEmails(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  useEffect(() => {
    if (!props.tagFilters) return;
    APIClient.getEmailFilterOptions()
      .then(setFilterOptions)
      .catch(() => notification.error({ message: 'Failed to load email filters.' }));
  }, [props.tagFilters]);

  async function loadEmails(p: number) {
    setLoading(true);
    try {
      const resp = await APIClient.listEmails({ page: p, pageSize: PAGE_SIZE }, request);
      setEmails(resp.content);
      setTotal(resp.total);
      setPage(resp.currentPage || p);
    } catch (e) {
      notification.error({ message: 'Failed to list emails at this time.' });
    } finally {
      setLoading(false);
    }
  }

  const debouncedSearch = useMemo(
    () => debounce((value: string) => updateFiltersRef.current({ search: value }), 400),
    [],
  );

  const tagOptions = useMemo(() => {
    const tags = new Set([TagAutomation, ...(filterOptions?.tags ?? []), ...filters.tags]);
    return Array.from(tags)
      .sort()
      .map((t) => ({ value: t, label: t }));
  }, [filterOptions, filters.tags]);

  const classOptions = useMemo(() => {
    const classes = new Set([...(filterOptions?.classifications ?? []), ...filters.classifications]);
    return Array.from(classes).map((c) => ({ value: c, label: classificationLabel(c) }));
  }, [filterOptions, filters.classifications]);

  const selectedIndex = selectedEmailId != null && emails
    ? emails.findIndex((e) => e.id === selectedEmailId)
    : -1;

  const prevEmail = useCallback(() => {
    if (!emails || selectedIndex <= 0) return;
    setSelectedEmailId(emails[selectedIndex - 1].id);
  }, [emails, selectedIndex]);

  const nextEmail = useCallback(() => {
    if (!emails || selectedIndex < 0 || selectedIndex >= emails.length - 1) return;
    setSelectedEmailId(emails[selectedIndex + 1].id);
  }, [emails, selectedIndex]);

  const columns: Column<InboundEmail>[] = [
    {
      key: 'sentAt',
      title: 'Received',
      width: 170,
      render: (e) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {formatDateTime(e.sentAt)}
        </span>
      ),
    },
    {
      key: 'venue',
      title: 'Venue',
      width: 180,
      render: (e) =>
        e.venue ? (
          <span className="text-text-2 text-[12.5px]">
            {e.venue}
            {e.account && <span className="text-text-3"> · {e.account}</span>}
          </span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      key: 'subject',
      title: 'Subject',
      render: (e) => (
        <span className="text-text text-[12.5px]">
          {e.subject || <span className="text-text-3">(no subject)</span>}
        </span>
      ),
    },
    {
      key: 'classification',
      title: 'Classification',
      width: 210,
      render: (e) => <RoutingCell email={e} />,
    },
    {
      key: 'tags',
      title: 'Tags',
      render: (e) => (
        <div className="flex gap-1 flex-wrap">
          {e.tags.map((tag) => (
            <StatusTag key={tag} tone="accent" dot={false}>
              {tag}
            </StatusTag>
          ))}
        </div>
      ),
    },
    {
      key: 'attachments',
      title: <PaperClipOutlined />,
      width: 200,
      render: (e) => (
        <div className="flex flex-col gap-0.5">
          {e.attachments.map((a: InboundEmailAttachment) => (
            <a
              key={a.id}
              target="_blank"
              rel="noreferrer"
              href={APIClient.getAttachmentDownloadUrl(a)}
              onClick={(ev) => ev.stopPropagation()}
              className="text-accent text-[11.5px] truncate max-w-[200px] hover:opacity-80"
            >
              {a.filename}
            </a>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title={props.title ?? "Emails"}
        subtitle={
          props.description ?? (props.filterTag
            ? `Inbound emails tagged \`${props.filterTag}\`.`
            : 'All inbound parsed emails across venues.')
        }
      />
      <PageToolbar>
        <input
          type="text"
          placeholder="Search emails…"
          defaultValue={search}
          onChange={(e) => debouncedSearch(e.target.value)}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[280px] text-[12.5px] text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />
        <SelectPill
          label="Disposition"
          value={dispositionOptions.find((o) => o.value === disposition)?.label ?? 'Any'}
          options={dispositionOptions}
          selected={disposition}
          onChange={(value) => updateFilters({ disposition: value })}
        />
        {props.tagFilters && (
          <>
            <MultiSelectPill
              label="Tags"
              options={tagOptions}
              selected={filters.tags}
              onChange={(tags) => updateFilters({ tags })}
            />
            <MultiSelectPill
              label="Class"
              options={classOptions}
              selected={filters.classifications}
              onChange={(classifications) => updateFilters({ classifications })}
            />
            <label className="flex items-center gap-1.5 text-[12.5px] text-text-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filters.hideAutomation}
                onChange={(e) => updateFilters({ hideAutomation: e.target.checked })}
              />
              Hide automation
            </label>
          </>
        )}
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {emails ? (
            <>
              total <b className="text-text-2">{total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={emails ?? []}
        rowKey={(e) => String(e.id)}
        loading={loading}
        emptyMessage="No emails found."
        onRowClick={(e) => setSelectedEmailId(e.id)}
        rowHref={(e) => `/emails/${e.id}`}
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{emails?.length ?? 0}</b> of{' '}
            <b className="text-text-2">{total.toLocaleString()}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(p) => loadEmails(p)}
            loading={loading}
          />
        }
      />

      {selectedEmailId != null && (
        <EmailDetailModal
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
          onTagsChanged={() => loadEmails(page)}
          onPrev={selectedIndex > 0 ? prevEmail : undefined}
          onNext={emails && selectedIndex >= 0 && selectedIndex < emails.length - 1 ? nextEmail : undefined}
        />
      )}
    </>
  );
};

export default Emails;
